import EditIcon from "@mui/icons-material/Edit";
import LogoutIcon from "@mui/icons-material/Logout";
import HomeIcon from "@mui/icons-material/Home";
import QrCodeIcon from "@mui/icons-material/QrCode";
import LanguageIcon from "@mui/icons-material/Language";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from "@mui/material";
import { LANGUAGES } from "components/views/language-selector/languages";
import { RoutePaths } from "constants/RoutePaths";
import { MyTrainingsViewContext } from "context/myTrainings/MyTrainingsViewContext";
import { useLogout } from "hooks/useLogout";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const StyledDrawer = styled(Drawer)({
  "& .MuiDrawer-paper": {
    width: 260,
    height: "fit-content",
    marginTop: 10,
    marginRight: 10,
    borderTopRightRadius: 8,
    borderTopLeftRadius: 8,
    borderBottomRightRadius: 8,
    borderBottomLeftRadius: 8,
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
  },
});

const StyledList = styled(List)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));

const LogoutListItemText = styled(ListItemText)(({ theme }) => ({
  "& .MuiListItemText-primary": {
    color: theme.palette.error.main,
  },
}));

const DrawerViewport = styled(Box)({
  width: 260,
  overflow: "hidden",
});

const DrawerContent = styled(Box)<{ $languageSubmenuOpen: boolean }>(
  ({ $languageSubmenuOpen }) => ({
    width: 520,
    display: "flex",
    transform: $languageSubmenuOpen ? "translateX(0)" : "translateX(-260px)",
    transition: "transform 0.28s ease",
  }),
);

const DrawerPane = styled(Box)({
  width: 260,
  flexShrink: 0,
});

type Props = {
  open: boolean;
  onClose: () => void;
};

export const MobileMenuComponent = ({ open, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const logout = useLogout();
  const { user, isStatisticOfLoggedUser } = useContext(MyTrainingsViewContext);
  const [isLanguageSubmenuOpen, setIsLanguageSubmenuOpen] = useState(false);

  const currentLanguage = (i18n.language || "pl").toLowerCase().split("-")[0];

  const handleDrawerClose = () => {
    setIsLanguageSubmenuOpen(false);
    onClose();
  };

  const handleEditProfileClick = () => {
    handleDrawerClose();
    navigate(
      isStatisticOfLoggedUser
        ? RoutePaths.PROFILE
        : `${RoutePaths.PROFILE}?userId=${user?.id}`,
    );
  };

  const handleQrClick = () => {
    handleDrawerClose();
    navigate(RoutePaths.HOME_QR);
  };

  const handleHomeClick = () => {
    handleDrawerClose();
    navigate(RoutePaths.HOME);
  };

  const handleLogoutClick = async () => {
    handleDrawerClose();
    await logout();
    navigate(RoutePaths.LOGIN);
  };

  const handleLanguageClick = async (languageCode: string) => {
    await i18n.changeLanguage(languageCode);
    handleDrawerClose();
  };

  return (
    <StyledDrawer anchor="right" open={open} onClose={handleDrawerClose}>
      <DrawerViewport>
        <DrawerContent $languageSubmenuOpen={isLanguageSubmenuOpen}>
          <DrawerPane>
            <StyledList>
              <StyledListItemButton onClick={() => setIsLanguageSubmenuOpen(false)}>
                <ListItemIcon>
                  <ArrowBackIosNewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t("mobileMenu.changeLanguage")} />
              </StyledListItemButton>

              {LANGUAGES.map((language) => (
                <StyledListItemButton
                  key={language.code}
                  onClick={() => handleLanguageClick(language.code)}
                >
                  <ListItemIcon>
                    {currentLanguage === language.code ? (
                      <CheckIcon fontSize="small" />
                    ) : null}
                  </ListItemIcon>
                  <ListItemText primary={language.label} />
                </StyledListItemButton>
              ))}
            </StyledList>
          </DrawerPane>

          <DrawerPane>
            <StyledList>
              <StyledListItemButton onClick={handleHomeClick}>
                <ListItemIcon>
                  <HomeIcon />
                </ListItemIcon>
                <ListItemText primary={t("mobileMenu.home")} />
              </StyledListItemButton>

              <StyledListItemButton onClick={handleEditProfileClick}>
                <ListItemIcon>
                  <EditIcon />
                </ListItemIcon>
                <ListItemText primary={t("myTrainings.editProfile")} />
              </StyledListItemButton>

              <StyledListItemButton onClick={handleQrClick}>
                <ListItemIcon>
                  <QrCodeIcon />
                </ListItemIcon>
                <ListItemText primary={t("mobileMenu.loginToVrWithQr")} />
              </StyledListItemButton>

              <StyledListItemButton onClick={() => setIsLanguageSubmenuOpen(true)}>
                <ListItemIcon>
                  <LanguageIcon />
                </ListItemIcon>
                <ListItemText primary={t("mobileMenu.changeLanguage")} />
                <ArrowForwardIosIcon fontSize="small" />
              </StyledListItemButton>

              <StyledListItemButton onClick={handleLogoutClick}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <LogoutListItemText primary={t("navbar.logout")} />
              </StyledListItemButton>
            </StyledList>
          </DrawerPane>
        </DrawerContent>
      </DrawerViewport>
    </StyledDrawer>
  );
};
